import { useNavigate } from "react-router-dom";
import { Sparkles, SlidersHorizontal } from "lucide-react";
import { setStudioMode, proStudioPath, creatorPath } from "@/lib/studioMode";

/**
 * Simple | Advanced studio switch. `current` is the door the user is IN right
 * now; flipping remembers the choice (drives `/` on the next visit) and
 * navigates without losing the current song. The internal mode value stays
 * "pro" for storage compatibility — "Pro" in the UI means the paid membership.
 */
export default function ModeSwitch({ current = "simple", className = "", songId = undefined }) {
  const navigate = useNavigate();

  const pick = (next) => {
    if (next === current) return;
    setStudioMode(next);
    navigate(next === "pro" ? proStudioPath(songId) : creatorPath());
  };

  // Icons only below lg so the header fits a phone; labels return on laptops.
  const base = "flex items-center gap-1.5 rounded-full px-2.5 py-1.5 font-display text-[13px] font-semibold tracking-wide transition-colors duration-200 whitespace-nowrap lg:px-3.5";
  return (
    <div
      data-testid="mode-switch"
      data-current={current}
      role="radiogroup"
      aria-label="Simple or Advanced studio"
      title="Simple: guided steps. Advanced: the full 64-channel studio."
      className={`inline-flex items-center rounded-full border border-white/15 bg-black/40 p-1 backdrop-blur-xl ${className}`}
    >
      <button
        type="button"
        role="radio"
        aria-checked={current === "simple"}
        data-testid="mode-switch-simple"
        aria-label="Simple"
        onClick={() => pick("simple")}
        className={`${base} ${current === "simple" ? "bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-[0_0_18px_rgba(99,102,241,0.45)]" : "text-white/75 hover:text-white"}`}
      >
        <Sparkles className="h-4 w-4 lg:h-3.5 lg:w-3.5" /><span className="hidden lg:inline">Simple</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={current === "pro"}
        data-testid="mode-switch-pro"
        aria-label="Advanced"
        onClick={() => pick("pro")}
        className={`${base} ${current === "pro" ? "bg-white/[0.16] text-white" : "text-white/75 hover:text-white"}`}
      >
        <SlidersHorizontal className="h-4 w-4 lg:h-3.5 lg:w-3.5" /><span className="hidden lg:inline">Advanced</span>
      </button>
    </div>
  );
}
